import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle
} from '@fluentui/react-components';
import { Trans, useTranslation } from 'react-i18next';

// models
import {
  CreatePersonPhoneCommand,
  UpdatePersonPhoneCommand
} from '../../../models/Entities/Person/Phone/PhoneModels';

// components
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';

// functions
import * as ownFunctions from './PersonPhone.Form.Functions';

interface FormValidation {
  success: boolean;
  errors: string[];
}

const PersonPhoneFormComponent = (props: ownFunctions.Props) => {
  const [t] = useTranslation();

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [validation, setValidation] = useState<FormValidation | undefined>();
  const [phoneNumber, setPhoneNumber] = useState<string>('');

  useEffect(() => {
    if (props.phone) {
      setPhoneNumber(props.phone.phoneNumber || '');
    } else {
      resetForm();
    }
  }, [props.phone, props.isOpen]);

  useEffect(() => {
    if (submitted && (props.successAdding || props.successUpdating)) {
      resetForm();
      props.dismissPanel();
    }
  }, [props.successAdding, props.successUpdating]);

  const resetForm = () => {
    setPhoneNumber('');
    setSubmitted(false);
    setValidation(undefined);
  };

  const validate = (): boolean => {
    const errors: string[] = [];

    if (!phoneNumber.trim()) {
      errors.push(t('persons.phones.form.validation.required', { defaultValue: 'El teléfono es obligatorio' }));
    }

    const result: FormValidation = { success: errors.length === 0, errors };
    setValidation(result);
    return result.success;
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!validate()) return;

    const payload: CreatePersonPhoneCommand = {
      personId: props.PersonId,
      phoneNumber: phoneNumber.trim()
    };

    if (props.phone) {
      props.UpdatePhone({ ...payload, id: props.phone.id } as UpdatePersonPhoneCommand);
    } else {
      props.CreatePhone(payload);
    }
  };

  const handleCancel = () => {
    props.dismissPanel();
    resetForm();
  };

  return (
    <Drawer
      open={props.isOpen}
      type="overlay"
      position="end"
      size="small"
      onOpenChange={(_, data) => {
        if (!data.open) handleCancel();
      }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle>
          {props.phone
            ? t('persons.phones.form.editTitle', { defaultValue: 'Editar teléfono' })
            : t('persons.phones.form.addTitle', { defaultValue: 'Nuevo teléfono' })}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        {submitted && validation && !validation.success && (
          <MessageBar intent="error" layout="multiline" style={{ marginBottom: 12 }}>
            <MessageBarBody>
              <MessageBarTitle>
                {t('persons.form.review', { defaultValue: 'Revise la información ingresada' })}
              </MessageBarTitle>
              <ul>
                {validation.errors.map((error: string, index: number) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </MessageBarBody>
          </MessageBar>
        )}

        {submitted && (props.failAdding || props.failUpdating) && props.error && (
          <ErrorMessageComponent Error={props.error} />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field
            label={t('persons.phones.form.phoneNumber', { defaultValue: 'Número de teléfono' })}
            required
            validationMessage={
              submitted && !phoneNumber.trim()
                ? t('persons.phones.form.validation.required', { defaultValue: 'El teléfono es obligatorio' })
                : undefined
            }
            validationState={submitted && !phoneNumber.trim() ? 'error' : undefined}
          >
            <Input type="tel" value={phoneNumber} onChange={(_, data) => setPhoneNumber(data.value || '')} />
          </Field>
        </div>
      </DrawerBody>

      <DrawerFooter>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start', width: '100%' }}>
          <Button appearance="secondary" onClick={handleCancel}>
            <Trans i18nKey={'persons.form.footer.cancel'} defaultValue="Cancelar" />
          </Button>
          <Button appearance="primary" onClick={handleSave} disabled={props.isAdding || props.isUpdating}>
            <Trans i18nKey={'persons.form.footer.save'} defaultValue="Guardar" />
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(PersonPhoneFormComponent as any);
